import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  getExpediente, revisarDocumento, cambiarEtapa, addActividad,
  crearPropuesta, listarPropuestas, fetchStaffDocBlob, formatErr,
} from "@/lib/staffApi";
import { toast } from "sonner";
import { Loader2, ArrowLeft, FileText, Check, X, Eye } from "lucide-react";

const MOTIVOS = ["Ingreso insuficiente", "Buró", "Antigüedad", "Enganche", "Edad", "Documentación", "Política de unidad", "Otro"];
const ESTADO_COLOR = {
  Pendiente: "text-neutral-500", Cargado: "text-sky-400", Aceptado: "text-emerald-400", Rechazado: "text-red-400",
};

export default function AsesorExpediente() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [exp, setExp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [propuestas, setPropuestas] = useState([]);
  const [rejectDoc, setRejectDoc] = useState(null);
  const [motivo, setMotivo] = useState(MOTIVOS[0]);
  const [nota, setNota] = useState("");
  const [prop, setProp] = useState({ institucion: "", producto: "", modalidad: "", enganche: "", plazo: "", tasa: "", mensualidad_estimada: "", condiciones_adicionales: "" });
  const [publishing, setPublishing] = useState(false);
  const propKeyRef = useRef(null);

  const load = useCallback(() => {
    setLoading(true);
    getExpediente(id)
      .then((e) => { setExp(e); setProp((p) => ({ ...p, modalidad: e.modalidad, institucion: e.institucion || "", producto: e.producto_nombre || "" })); })
      .catch((e) => { toast.error(formatErr(e.response?.data?.detail)); navigate("/staff/bandeja"); })
      .finally(() => setLoading(false));
    listarPropuestas(id).then(setPropuestas).catch(() => {});
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  const verDoc = async (doc) => {
    try {
      const url = await fetchStaffDocBlob(id, doc);
      window.open(url, "_blank");
    } catch (e) { toast.error("No se pudo abrir el documento."); }
  };

  const aceptar = async (doc) => {
    try {
      await revisarDocumento(id, doc, "Aceptado");
      toast.success("Documento aceptado");
      load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };

  const rechazar = async () => {
    try {
      await revisarDocumento(id, rejectDoc, "Rechazado", motivo);
      toast.success("Documento rechazado · se notificó al cliente");
      setRejectDoc(null);
      load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };

  const setEtapa = async (id_etapa) => {
    try {
      await cambiarEtapa(id, id_etapa, "");
      toast.success("Etapa actualizada · cliente notificado");
      load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };

  const guardarNota = async () => {
    if (!nota.trim()) return;
    try { await addActividad(id, nota.trim()); setNota(""); toast.success("Nota agregada"); load(); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };

  const publicarPropuesta = async () => {
    if (publishing) return;
    setPublishing(true);
    try {
      if (!propKeyRef.current) propKeyRef.current = `prop-${id}-${Date.now()}`;
      await crearPropuesta(id, { ...prop, idempotency_key: propKeyRef.current });
      toast.success("Propuesta publicada · cliente notificado (NOT-009)");
      propKeyRef.current = null;
      listarPropuestas(id).then(setPropuestas);
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
    finally { setPublishing(false); }
  };

  if (loading || !exp)
    return <div className="flex items-center gap-2 text-neutral-400"><Loader2 className="animate-spin" /> Cargando expediente…</div>;

  const c = exp.cliente;

  return (
    <div data-testid="asesor-expediente" className="max-w-5xl">
      <button onClick={() => navigate("/staff/bandeja")} className="flex items-center gap-2 text-sm text-neutral-400 hover:text-neutral-200 mb-4">
        <ArrowLeft size={16} /> Volver a bandeja
      </button>
      <div className="flex items-center gap-3 mb-6">
        <h1 className="text-2xl font-bold">{c.nombre}</h1>
        <span className="font-mono text-xs text-neutral-500">{exp.folio}</span>
        <span className="text-xs bg-neutral-800 px-2 py-1 rounded">{exp.modalidad}</span>
        <span className="text-xs bg-neutral-800 px-2 py-1 rounded">{exp.tipo_cliente || "—"}</span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <div className="bg-[#111] border border-neutral-800 rounded-xl p-5 lg:col-span-2">
          <div className="text-sm font-semibold text-neutral-300 mb-3">Datos del cliente</div>
          <div className="grid grid-cols-2 gap-y-2 text-sm">
            <Info label="Correo" value={c.email} />
            <Info label="Celular" value={c.celular} />
            <Info label="Modalidad (Q05)" value={c.modalidad_q05} />
            <Info label="Tipo cliente (Q09)" value={c.tipo_cliente_q09} />
            <Info label="Ingreso" value={c.ingreso} />
            <Info label="Enganche" value={c.enganche} />
            <Info label="Historial" value={c.historial} />
            <Info label="Rango edad" value={c.rango_edad} />
            <Info label="Vehículo interés" value={c.vehiculo_interes} />
          </div>
        </div>
        <div className="bg-[#111] border border-neutral-800 rounded-xl p-5">
          <div className="text-sm font-semibold text-neutral-300 mb-3">Precalificación (Fase 2)</div>
          <div className="text-sm space-y-2">
            <Info label="Perfil" value={exp.perfil} />
            <Info label="Score" value={exp.score_total != null ? String(exp.score_total) : "—"} />
            <Info label="Compatibles" value={String((exp.productos_compatibles || []).length)} />
            {exp.requiere_revision_manual && <div className="text-amber-400 text-xs">Requiere revisión manual</div>}
          </div>
        </div>
      </div>

      {/* Stepper */}
      <div className="bg-[#111] border border-neutral-800 rounded-xl p-5 mb-6" data-testid="expediente-stepper">
        <div className="text-sm font-semibold text-neutral-300 mb-3">Etapa del trámite</div>
        <div className="flex flex-wrap gap-2">
          {exp.etapas.map((s) => (
            <button key={s.id} onClick={() => setEtapa(s.id)} data-testid={`etapa-${s.id}`}
              className={`px-3 py-2 rounded-lg text-sm border transition-colors ${
                exp.etapa_actual === s.id
                  ? "bg-orange-500 text-black border-orange-500 font-semibold"
                  : "bg-[#0a0a0a] border-neutral-700 text-neutral-300 hover:border-orange-500"
              }`}>
              {s.orden}. {s.etapa}
            </button>
          ))}
        </div>
      </div>

      {/* Documentos */}
      <div className="bg-[#111] border border-neutral-800 rounded-xl p-5 mb-6" data-testid="expediente-documentos">
        <div className="text-sm font-semibold text-neutral-300 mb-3">Documentos ({exp.documentos.length})</div>
        {exp.documentos.length === 0 && <div className="text-sm text-neutral-500">Sin checklist documental para este trámite.</div>}
        <div className="space-y-2">
          {exp.documentos.map((d) => (
            <div key={d.id_documento} data-testid={`doc-${d.id_documento}`}
              className="flex items-center justify-between bg-[#0a0a0a] border border-neutral-800 rounded-lg px-4 py-3">
              <div className="flex items-center gap-3">
                <FileText size={18} className="text-neutral-500" />
                <div>
                  <div className="text-sm">{d.documento}</div>
                  <div className={`text-xs ${ESTADO_COLOR[d.estado] || "text-neutral-500"}`}>
                    {d.estado}{d.motivo_rechazo ? ` · ${d.motivo_rechazo}` : ""}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {d.estado !== "Pendiente" && (
                  <button onClick={() => verDoc(d.id_documento)} data-testid={`doc-view-${d.id_documento}`}
                    className="p-2 rounded-lg bg-neutral-900 hover:bg-neutral-800 text-neutral-300"><Eye size={16} /></button>
                )}
                {d.estado === "Cargado" && (
                  <>
                    <button onClick={() => aceptar(d.id_documento)} data-testid={`doc-accept-${d.id_documento}`}
                      className="p-2 rounded-lg bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-400"><Check size={16} /></button>
                    <button onClick={() => { setRejectDoc(d.id_documento); setMotivo(MOTIVOS[0]); }}
                      data-testid={`doc-reject-${d.id_documento}`}
                      className="p-2 rounded-lg bg-red-500/15 hover:bg-red-500/25 text-red-400"><X size={16} /></button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Propuestas */}
      <div className="bg-[#111] border border-neutral-800 rounded-xl p-5 mb-6" data-testid="expediente-propuestas">
        <div className="text-sm font-semibold text-neutral-300 mb-3">Publicar propuesta (NOT-009)</div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {[
            ["institucion", "Institución"], ["producto", "Producto"], ["modalidad", "Modalidad"],
            ["enganche", "Enganche"], ["plazo", "Plazo (meses)"], ["tasa", "Tasa"],
            ["mensualidad_estimada", "Mensualidad estimada"],
          ].map(([k, label]) => (
            <div key={k}>
              <label className="text-xs text-neutral-500">{label}</label>
              <input value={prop[k]} onChange={(e) => setProp({ ...prop, [k]: e.target.value })}
                data-testid={`prop-${k}`}
                className="mt-1 w-full bg-[#0a0a0a] border border-neutral-700 rounded-lg px-3 py-2 text-sm focus:border-orange-500 outline-none" />
            </div>
          ))}
          <div className="col-span-2 md:col-span-3">
            <label className="text-xs text-neutral-500">Condiciones adicionales</label>
            <input value={prop.condiciones_adicionales} onChange={(e) => setProp({ ...prop, condiciones_adicionales: e.target.value })}
              data-testid="prop-condiciones"
              className="mt-1 w-full bg-[#0a0a0a] border border-neutral-700 rounded-lg px-3 py-2 text-sm focus:border-orange-500 outline-none" />
          </div>
        </div>
        <button onClick={publicarPropuesta} data-testid="prop-submit" disabled={publishing}
          className="mt-4 bg-orange-500 hover:bg-orange-600 disabled:opacity-60 text-black font-semibold rounded-lg px-4 py-2 text-sm">
          {publishing ? "Publicando…" : "Publicar propuesta"}
        </button>
        {propuestas.length > 0 && (
          <div className="mt-4 space-y-2">
            <div className="text-xs text-neutral-500">Historial de versiones (inmutables)</div>
            {propuestas.map((p) => (
              <div key={p.id} className="text-sm bg-[#0a0a0a] border border-neutral-800 rounded-lg px-3 py-2 flex justify-between">
                <span>v{p.version} · {p.institucion} · {p.producto}</span>
                <span className="text-neutral-500">{p.tasa} · {p.plazo}m</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Actividades */}
      <div className="bg-[#111] border border-neutral-800 rounded-xl p-5" data-testid="expediente-actividades">
        <div className="text-sm font-semibold text-neutral-300 mb-3">Notas / actividades</div>
        <div className="flex gap-2">
          <input value={nota} onChange={(e) => setNota(e.target.value)} placeholder="Agregar nota interna…"
            data-testid="actividad-input"
            className="flex-1 bg-[#0a0a0a] border border-neutral-700 rounded-lg px-3 py-2 text-sm focus:border-orange-500 outline-none" />
          <button onClick={guardarNota} data-testid="actividad-submit"
            className="bg-neutral-800 hover:bg-neutral-700 rounded-lg px-4 py-2 text-sm">Agregar</button>
        </div>
        <div className="mt-3 space-y-2">
          {(exp.actividades || []).map((a) => (
            <div key={a.id} className="text-sm text-neutral-400 border-l-2 border-neutral-700 pl-3">
              {a.nota} <span className="text-xs text-neutral-600">· {a.autor}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Reject modal */}
      {rejectDoc && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" data-testid="reject-modal">
          <div className="bg-[#131313] border border-neutral-800 rounded-2xl p-6 w-full max-w-md">
            <div className="text-lg font-bold mb-3">Rechazar documento</div>
            <label className="text-xs text-neutral-400">Motivo de rechazo (obligatorio)</label>
            <select value={motivo} onChange={(e) => setMotivo(e.target.value)} data-testid="reject-motivo"
              className="mt-1 w-full bg-[#0a0a0a] border border-neutral-700 rounded-lg px-3 py-2.5 text-sm">
              {MOTIVOS.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
            <div className="flex gap-2 mt-5">
              <button onClick={() => setRejectDoc(null)} className="flex-1 bg-neutral-800 hover:bg-neutral-700 rounded-lg py-2 text-sm">Cancelar</button>
              <button onClick={rechazar} data-testid="reject-confirm"
                className="flex-1 bg-red-500 hover:bg-red-600 text-white rounded-lg py-2 text-sm font-semibold">Rechazar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <div className="text-xs text-neutral-500">{label}</div>
      <div className="text-neutral-200">{value || "—"}</div>
    </div>
  );
}
